import { Injectable } from "@angular/core";
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse,
} from "@angular/common/http";
import {
  catchError,
  Observable,
  throwError as observableThrowError,
} from "rxjs";
import { environment } from "src/environments/environment";
import { AppToastService } from "../services/app-toast.service";
import {
  ResponseProblemDetails,
  ResponseTemplate,
} from "../models/account/api.common";
import { AppConfigService } from "../services/app-config.service";

type ResponseError =
  | string
  | string[]
  | ResponseTemplate
  | ResponseProblemDetails
  | null;

@Injectable()
export class HttpErrorInterceptor implements HttpInterceptor {
  constructor(private toast: AppToastService, private appConfig: AppConfigService) { }

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    if (!request.url.startsWith(environment.url)) return next.handle(request);

    return next.handle(request).pipe(
      catchError((err: HttpErrorResponse) => {
        // trata 401 (Unauthorized) - Redireciona para login se não for tratado pelo JwtInterceptor
        if (err.status === 401) {
          // O JwtInterceptor geralmente trata isso, mas como fallback:
          // Se chegamos aqui, o JwtInterceptor falhou ou não pegou.
        }

        // trata 403 (Forbidden) - Acesso Negado
        if (err.status === 403) {
          this.toast.show("Acesso Negado", "Você não tem permissão para realizar esta ação.");
        }

        // ignora as respostas de erro fora do range de 400 - 499 para excluir erros de servidor
        if (err.status >= 400 && err.status !== 401 && err.status !== 403 && err.status < 500) {
          this.displayErrorMessage(err.error);
        }

        if ((err.status >= 500 && err.status < 600) || err.status === 0) {
          this.toastServerError();
        }

        return observableThrowError(() => err);
      })
    );
  }

  toastServerError() {
    this.toast.show("Erro", "Falha na comunicação com o servidor.");
  }

  toastDefaultError() {
    this.toast.show("Erro", "Falha na operação.");
  }

  toastStringError(error: string) {
    // verifica se não é string vazia
    if (error.length > 0) this.toast.show("Erro", error);
    else this.toastDefaultError();
  }

  toastStringArrayError(error: string[]) {
    // verifica se o array tem um item e não é string vazia
    if (error.length > 0 && error[0].length > 0)
      this.toast.show("Erro", error[0]);
    else this.toastDefaultError();
  }

  /**
   * Exibe mensagem de erro da API dentro do Toast. Alterar a ordem das condições muda a prioridade de cada tratamento, por exemplo:
   *
   * {
   * "message": "Falha ao efetuar login de usuário",
   * "validationErrors": ["Usuário não encontrado"]
   * }
   *
   * O campo **validationErrors** é verificado antes de **message**, sendo assim **validationErrors** tem prioridade.
   */
  private displayErrorMessage(error: ResponseError): void {

    
    if (!error) {
      return this.toastDefaultError();
    }

    if (typeof error === "string") {
      return this.toastStringError(error);
    }

    if (Array.isArray(error)) {
      return this.toastStringArrayError(error);
    }

    if ("validationErrors" in error && error.validationErrors) {
      return this.toastStringArrayError(error.validationErrors);
    }

    if ("error" in error && error.error) {
      return typeof error.error === "string"
        ? this.toastStringError(error.error)
        : this.toastStringArrayError(error.error);
    }

    if ("message" in error && error.message) {
      return this.toastStringError(error.message);
    }

    // IGNORA O ResponseProblemDetail
    // Essa resposta é gerada nativamente no DOTNET e não deve ser utilizada

    // se nenhum dos casos acontecerem, exibe uma mensagem de erro padrão
    return this.toastDefaultError();
  }
}
